/* eslint-disable react-hooks/exhaustive-deps */
import { AnimatePresence, motion } from "framer-motion";

import { RefObject, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import Spinner from "@/components/helpers/Spinner/Spinner";

import ContentRequests from "@/api/content/content.requests";

import Controller from "../Controller";
import { IHlsPreparedThumbnail } from "../hls_manager/hls_models";
import { usePlayerControls2 } from "../hooks/PlayerControlsContext";
import { usePlayerControls } from "../hooks/usePlayerControls";
import { QualityLevel } from "../useHls";
import BottomSideOfPlayer from "./BottomSideOfPlayer";
import TopSideOfPlayer from "./TopSideOfPlayer";

export type PlayerUIProps = {
  videoRef: RefObject<HTMLVideoElement>;
  thumbnail?: IHlsPreparedThumbnail | null;
  currentQuality?: number | "auto";
  setQuality?: (index: number | "auto") => void;
  qualities?: QualityLevel[];
  loading?: boolean;
  audioTracks?: { id: number; name: string }[];
  setAudioTrack?: (index: number) => void;
  currentAudioTrack?: number;
};

const PlayerUI = ({
  videoRef,
  thumbnail,
  currentQuality,
  setQuality,
  qualities,
  loading,
  audioTracks,
  setAudioTrack,
  currentAudioTrack,
}: PlayerUIProps) => {
  const { showControls, setShowControls, controlsLock } = usePlayerControls2();
  const { isPlaying } = usePlayerControls(videoRef);
  const { id, episodeId } = useParams() as { id: string; episodeId: string };

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const videoContainer = document.getElementById("player-ui-wrapper");

    const enableMouseMove = () => {
      if (!controlsLock) return;
      setShowControls(true);

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        if (isPlaying) {
          setShowControls(false);
        }
      }, 3000);
    };

    if (videoContainer) {
      videoContainer.addEventListener("mousemove", enableMouseMove);
    }

    return () => {
      if (videoContainer) {
        videoContainer.removeEventListener("mousemove", enableMouseMove);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [controlsLock, isPlaying, showControls]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (showControls && isPlaying && controlsLock) {
      timeout = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }

    return () => clearTimeout(timeout);
  }, [showControls, isPlaying, controlsLock]);

  useEffect(() => {
    const statisticInterval = setInterval(() => {
      const formData = new FormData();
      if (episodeId) {
        formData.append("episode_id", episodeId);
      }
      formData.append("content_id", id);
      formData.append("time_code", Math.floor(videoRef.current?.currentTime || 0).toString() || "0");
      if (!videoRef.current) return;
      if (!videoRef.current?.paused) {
        ContentRequests.postContentStatistic(formData);
      }
    }, 10000);
    return () => clearInterval(statisticInterval);
  }, []);

  return (
    <div id="player-ui-wrapper">
      <Controller videoRef={videoRef} />

      <motion.div
        initial={false}
        animate={{
          opacity: showControls ? 1 : 0,
        }}
        className="absolute left-0 top-0 z-[100] h-full w-full"
        style={{
          background: "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, #000 100%)",
        }}
      ></motion.div>

      <div className="absolute left-0 top-0 z-[100] flex h-full w-full flex-col justify-between pb-[44px] pt-10 max-lg:pb-6 max-lg:pt-6">
        <AnimatePresence mode="wait">
          <motion.div
            initial={false}
            animate={{
              y: showControls ? 0 : -100,
              opacity: showControls ? 1 : 0,
              pointerEvents: showControls ? "auto" : "none",
            }}
            transition={{ duration: 0.2 }}
            className="px-6"
          >
            <TopSideOfPlayer videoRef={videoRef} />
          </motion.div>
        </AnimatePresence>

        {loading && (
          <div className="absolute left-1/2 top-1/2 z-[10000000] flex h-[120px] w-[120px] -translate-x-1/2 -translate-y-1/2 items-center justify-center max-lg:h-20 max-lg:w-20 ">
            <Spinner className="" />
          </div>
        )}
        <MiddleSideOfPlayer videoRef={videoRef} />

        <AnimatePresence mode="wait">
          <motion.div
            initial={false}
            animate={{
              y: showControls ? 0 : 100,
              opacity: showControls ? 1 : 0,
              pointerEvents: showControls ? "auto" : "none",
            }}
            transition={{ duration: 0.2 }}
            className="mt-3 px-6"
          >
            <BottomSideOfPlayer
              thumbnail={thumbnail}
              videoRef={videoRef}
              qualities={qualities}
              setQuality={setQuality}
              currentQuality={currentQuality}
              audioTracks={audioTracks}
              setAudioTrack={setAudioTrack}
              currentAudioTrack={currentAudioTrack}
            />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PlayerUI;

const MiddleSideOfPlayer = ({ videoRef }: { videoRef: RefObject<HTMLVideoElement> }) => {
  const { togglePlay } = usePlayerControls(videoRef);

  return <div onClick={togglePlay} className="h-full w-full" />;
};
